import log from 'loglevel';
import { BrowserLoggerConfig, getLoggerConfig } from '../../config/logger.config';

export interface LogEntry {
  level: string;
  message: string;
  timestamp: string;
  sessionId: string;
  userId?: string;
  url: string;
  metadata?: any;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

export class BrowserLogger {
  private static instance: BrowserLogger;
  private config: BrowserLoggerConfig;
  private sessionId: string;
  private userId?: string;
  private logBatch: LogEntry[] = [];
  private batchTimer?: number;
  private debugBuffer: LogEntry[] = []; // In-memory ring buffer for debugging only
  private readonly MAX_BATCH_SIZE = 10;
  private readonly BATCH_TIMEOUT = 2000; // 2 seconds
  private readonly DEBUG_BUFFER_SIZE = 100;

  private constructor(config: BrowserLoggerConfig) {
    this.config = config;
    this.sessionId = this.generateSessionId();
    this.initialize();
  }

  static getInstance(): BrowserLogger {
    if (!BrowserLogger.instance) {
      BrowserLogger.instance = new BrowserLogger(getLoggerConfig());
    }
    return BrowserLogger.instance;
  }

  private initialize(): void {
    log.setLevel(this.config.level);
    this.setupErrorHandlers();
    this.setupUnloadHandler();
    
    // Log initialization for debugging
    
    // Make logger available in console for debugging in development
    if (this.config.enableConsole) {
      (window as any).logger = this;
    }
  }

  // Core logging methods
  debug(message: string, metadata?: any): void {
    this.log('debug', message, this.redact(metadata));
  }

  info(message: string, metadata?: any): void {
    this.log('info', message, this.redact(metadata));
  }

  warn(message: string, metadata?: any): void {
    this.log('warn', message, this.redact(metadata));
  }

  error(message: string, error?: Error | any, metadata?: any): void {
    const errorData = error instanceof Error ? {
      name: error.name,
      message: error.message,
      stack: error.stack
    } : error;
    
    this.log('error', message, {
      ...this.redact(metadata),
      error: this.redact(errorData)
    });
  }

  // User-facing error with technical details
  userError(userMessage: string, technicalMessage: string, error?: Error): void {
    this.log('error', technicalMessage, {
      userMessage,
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : undefined
    });
  }

  private log(level: string, message: string, metadata?: any): void {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userId,
      url: window.location.pathname,
      metadata
    };

    // Add to debug buffer (ring buffer)
    this.addToDebugBuffer(entry);

    // Log to console in development
    if (this.config.enableConsole) {
      const logMethod = (log as any)[level] || log.info;
      logMethod(message, metadata);
    }

    // Skip remote logging in development unless explicitly enabled
    if (!this.config.enableRemote) {
      return;
    }

    // Filter by log level for remote sending
    if (!this.shouldSendLevel(level)) {
      return;
    }

    // Add to batch for remote logging
    this.addToBatch(entry);
  }

  private shouldSendLevel(level: string): boolean {
    const levels = ['debug', 'info', 'warn', 'error'];
    const configLevel = levels.indexOf(this.config.level);
    const entryLevel = levels.indexOf(level);
    return entryLevel >= configLevel;
  }

  private addToDebugBuffer(entry: LogEntry): void {
    this.debugBuffer.push(entry);
    if (this.debugBuffer.length > this.DEBUG_BUFFER_SIZE) {
      this.debugBuffer.shift();
    }
  }

  private addToBatch(entry: LogEntry): void {
    this.logBatch.push(entry);
    
    if (this.logBatch.length >= this.MAX_BATCH_SIZE) {
      this.flush();
    } else {
      this.scheduleBatch();
    }
  }

  private scheduleBatch(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }
    this.batchTimer = window.setTimeout(() => this.flush(), this.BATCH_TIMEOUT);
  }

  async flush(): Promise<void> {
    if (this.logBatch.length === 0) return;
    
    const batch = [...this.logBatch];
    this.logBatch = [];
    
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = undefined;
    }

    await this.sendLogs(batch);
  }

  private async sendLogs(logs: LogEntry[]): Promise<void> {
    const payload = {
      logs,
      context: {
        appVersion: process.env.REACT_APP_VERSION || 'unknown',
        environment: process.env.NODE_ENV,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      }
    };

    const url = this.config.remoteUrl || '/api/logs';
    const data = JSON.stringify(payload);
    

    // Try sendBeacon first (best for unload events)
    if (navigator.sendBeacon && data.length < 65536) { // 64KB limit
      const blob = new Blob([data], { type: 'application/json' });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) {
        return;
      }
    }

    // Fallback to fetch with keepalive
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: data,
        keepalive: true
      });
    } catch (error) {
      // Fire and forget - don't block UI
      console.warn('[BrowserLogger] Failed to send logs:', error);
    }
  }

  // Redaction utilities
  private redact(data: any): any {
    if (!this.config.enableRedaction) {
      return data;
    }

    if (typeof data === 'string') {
      // Remove emails
      data = data.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL]');
      // Remove JWT tokens
      data = data.replace(/Bearer\s+[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*/g, 'Bearer [TOKEN]');
      // Remove API keys
      data = data.replace(/api[_-]?key[_-]?[=:]\s*[A-Za-z0-9-_]+/gi, 'api_key=[REDACTED]');
      // Remove URLs with query params
      try {
        data = data.replace(/https?:\/\/[^\s]+\?[^\s]+/g, (match: string) => {
          const url = new URL(match);
          return `${url.origin}${url.pathname}?[PARAMS]`;
        });
      } catch {
        // If URL parsing fails, leave as is
      }
    }

    if (typeof data === 'object' && data !== null) {
      const redacted: any = Array.isArray(data) ? [] : {};
      for (const key in data) {
        const lowerKey = key.toLowerCase();
        if (['password', 'token', 'secret', 'apikey', 'api_key', 'authorization'].includes(lowerKey)) {
          redacted[key] = '[REDACTED]';
        } else {
          redacted[key] = this.redact(data[key]);
        }
      }
      return redacted;
    }

    return data;
  }

  // Event handlers
  private setupErrorHandlers(): void {
    window.addEventListener('error', (event) => {
      this.error('Uncaught error', new Error(event.message), {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.error('Unhandled promise rejection', new Error(String(event.reason)));
    });
  }

  private setupUnloadHandler(): void {
    // Flush logs before page unload
    window.addEventListener('beforeunload', () => {
      this.flush();
    });

    // Flush logs when page becomes hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.flush();
      }
    });
  }

  // Utility methods
  setUser(userId: string, metadata?: any): void {
    this.userId = userId;
    this.info('User context updated', { userId, ...metadata });
  }

  clearUser(): void {
    this.userId = undefined;
    this.info('User context cleared');
  }

  getSessionId(): string {
    return this.sessionId;
  }

  getUserId(): string | undefined {
    return this.userId;
  }

  getLevel(): string {
    return this.config.level;
  }

  getRequestHeaders(): Record<string, string> {
    return {
      'X-Session-ID': this.sessionId,
      'X-User-ID': this.userId || 'anonymous'
    };
  }

  getDebugLogs(): LogEntry[] {
    return [...this.debugBuffer];
  }

  getLogBuffer(): LogEntry[] {
    return this.getDebugLogs(); // Alias for compatibility
  }

  exportLogs(): string {
    return JSON.stringify(this.getDebugLogs(), null, 2);
  }

  downloadLogs(): void {
    const logs = this.getDebugLogs();
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  clearLogs(): void {
    this.debugBuffer = [];
    this.info('Debug buffer cleared');
  }

  private generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // API tracking helpers
  logApiCall(method: string, url: string, data?: any): void {
    this.debug(`API ${method} ${url}`, { api: { method, url, data } });
  }

  logApiResponse(method: string, url: string, status: number, data?: any): void {
    const level = status >= 400 ? 'error' : 'debug';
    this.log(level, `API Response ${method} ${url} - ${status}`, { 
      api: { method, url, status, data } 
    });
  }

  trackAPI(method: string, url: string, status: number, duration: number, error?: Error): void {
    const level = status >= 400 ? 'error' : 'debug';
    this.log(level, `API ${method} ${url}`, {
      api: { method, url, status, duration },
      error: error ? { message: error.message, stack: error.stack } : undefined
    });
  }

  trackEvent(event: string, data?: any): void {
    this.info(`Event: ${event}`, { event, ...data });
  }

  logUserAction(action: string, data?: any): void {
    this.info(`User Action: ${action}`, { action, ...data });
  }

  logPerformance(metric: string, value: number): void {
    this.debug(`Performance: ${metric}`, { metric, value });
  }

  startTimer(label: string): () => number {
    const start = performance.now();
    return () => {
      const duration = Math.round(performance.now() - start);
      this.logPerformance(label, duration);
      return duration;
    };
  }

  // Compatibility methods (from old implementation)
  getStoredLogs(): LogEntry[] {
    // Return debug buffer instead of localStorage
    return this.getDebugLogs();
  }

  clearStoredLogs(): void {
    // Clear debug buffer instead of localStorage
    this.clearLogs();
  }

  downloadDebugLogs(): void {
    this.downloadLogs();
  }
}

// Export singleton instance
export const logger = BrowserLogger.getInstance();